/**
 * MultiSelect — popover-style multi-pick dropdown with searchable options.
 *
 * Used wherever a filter facet should support "any of these" rather than
 * exactly one. Replaces the single <select> filter chips on /customers,
 * /segments, /account-health so a CSM can see the combined book across
 * themselves + a partner, and a manager can pull the book across their
 * whole team.
 *
 * Why it's its own component (not a 3rd-party lib):
 *   - Tight visual integration with the .filter-bar styling
 *   - No transitive deps — keeps the JS bundle slim
 *   - We control the keyboard handling + outside-click + clear-all flow
 *
 * Selection model:
 *   - value is string[]; empty array = "any" (filter inactive)
 *   - onChange called with the new array on every check/uncheck
 *   - Mutates a local copy first so the caller can debounce or batch
 *
 * Visual:
 *   - Trigger looks like the other .filter-bar selects
 *   - Active state when value.length > 0 (pink highlighted border)
 *   - Trigger label: 'Account Executive' empty / 'Sarah Patterson' single /
 *     'Sarah Patterson +2' multiple / matches the placeholder when empty
 *   - Popover: search box + checkbox list + 'Clear (N)' + 'Done' actions
 */

import { useEffect, useMemo, useRef, useState } from 'react';

export interface MultiSelectProps {
  value: string[];
  onChange: (next: string[]) => void;
  options: string[];
  placeholder: string; // 'Account Executive', 'CSM', etc.
  minWidth?: number;
}

export function MultiSelect({
  value,
  onChange,
  options,
  placeholder,
  minWidth = 180,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (opt: string) => {
    if (selectedSet.has(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0];
    return `${value[0]} +${value.length - 1}`;
  }, [value, placeholder]);

  const active = value.length > 0;

  return (
    <div
      ref={ref}
      style={{ position: 'relative', display: 'inline-block', minWidth }}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-active={active ? '1' : '0'}
        title={value.length > 1 ? value.join(', ') : undefined}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '7px 28px 7px 10px',
          border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--fs-base)',
          fontFamily: 'inherit',
          color: active ? 'var(--accent-darker)' : 'var(--text-primary)',
          background: active ? 'var(--accent-light)' : 'var(--bg-card)',
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${active ? '%237E1F4B' : '%23897680'}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 8px center',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-1)',
          fontWeight: active ? 600 : 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {triggerLabel}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            minWidth: 260,
            maxWidth: 320,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-3)',
            padding: 8,
          }}
        >
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Filter ${placeholder.toLowerCase()}…`}
            style={{
              width: '100%',
              padding: '7px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--fs-base)',
              fontFamily: 'inherit',
              marginBottom: 6,
              outline: 'none',
            }}
          />
          <div
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              borderTop: '1px solid var(--border-light)',
              borderBottom: '1px solid var(--border-light)',
              margin: '4px 0',
            }}
          >
            {filtered.length === 0 ? (
              <div
                style={{
                  padding: '12px 10px',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-muted)',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                No matches.
              </div>
            ) : (
              filtered.map((opt) => {
                const checked = selectedSet.has(opt);
                return (
                  <label
                    key={opt}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 8px',
                      fontSize: 'var(--fs-base)',
                      cursor: 'pointer',
                      borderRadius: 4,
                      background: checked
                        ? 'var(--accent-light)'
                        : 'transparent',
                      color: checked
                        ? 'var(--accent-darker)'
                        : 'var(--text-primary)',
                      fontWeight: checked ? 600 : 500,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(opt)}
                      style={{
                        width: 14,
                        height: 14,
                        accentColor: 'var(--accent)',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {opt}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 4px 2px',
            }}
          >
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={value.length === 0}
              style={{
                background: 'none',
                border: 'none',
                color:
                  value.length === 0
                    ? 'var(--text-faint)'
                    : 'var(--text-muted)',
                fontSize: 'var(--fs-sm)',
                fontWeight: 600,
                cursor: value.length === 0 ? 'default' : 'pointer',
                padding: '4px 6px',
                fontFamily: 'inherit',
              }}
            >
              Clear {value.length > 0 ? `(${value.length})` : ''}
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setQuery('');
              }}
              style={{
                background: 'var(--accent)',
                border: 'none',
                color: '#fff',
                fontSize: 'var(--fs-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                padding: '5px 12px',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'inherit',
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
